"use client";

import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useStore } from '@/context/StoreContext';
import { formatCurrency as formatPriceUtil, formatDate } from '@/lib/formatters';
import { getSubdomain } from '@/lib/subdomain';
import { getApiBaseUrl } from '@/lib/api';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Link from 'next/link';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faBoxOpen, faRightFromBracket, faClock, faCheckCircle, 
    faTimesCircle, faTruck, faUser, faEnvelope, faLock, 
    faEye, faEyeSlash, faIdCard, faPhone, faShoppingBag, 
    faChevronRight, faShieldAlt, faCalendarAlt, faReceipt,
    faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';

export default function MinhaContaPage() {
    const { user, isAuthenticated, isLoadingAuth, login, register, logout, token } = useAuth();
    const { settings } = useStore();
    const formatPrice = (value: number) => formatPriceUtil(value, settings.currency);
    
    // Controle de telas e abas
    const [isLoginMode, setIsLoginMode] = useState(true);
    const [activeTab, setActiveTab] = useState<'pedidos' | 'perfil'>('pedidos');
    const [showLoginPassword, setShowLoginPassword] = useState(false);
    const [showRegisterPassword, setShowRegisterPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Formulários
    const [loginData, setLoginData] = useState({ login: '', password: '' });
    const [registerData, setRegisterData] = useState({ 
        name: '', 
        email: '', 
        document_number: '', 
        phone: '', 
        password: '' 
    });

    // Histórico de Pedidos
    const [orders, setOrders] = useState<any[]>([]);
    const [isLoadingOrders, setIsLoadingOrders] = useState(false);

    useEffect(() => {
        if (isAuthenticated && token) {
            fetchOrders();
        }
    }, [isAuthenticated, token]);

    const fetchOrders = async () => {
        setIsLoadingOrders(true);
        try {
            const subdomain = getSubdomain();
            
            const res = await fetch(`${getApiBaseUrl()}/public/client/orders`, {
                headers: { 
                    'X-Tenant-Subdomain': subdomain, 
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (res.ok) {
                setOrders(await res.json());
            }
        } catch (error) {
            console.error("Erro ao buscar pedidos", error);
        } finally {
            setIsLoadingOrders(false);
        }
    };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await login(loginData.login, loginData.password);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await register(registerData);
        } finally {
            setIsSubmitting(false);
        }
    };

    const getStatusBadge = (statusName: string, colorHex?: string) => {
        const name = statusName || 'Em Aberto';
        const hex = colorHex || '#4b5563'; 
        
        let icon = faClock;
        const lowerName = name.toLowerCase();
        
        if (lowerName.includes('conclu') || lowerName.includes('entreg') || lowerName.includes('finaliz') || lowerName.includes('pago')) {
            icon = faCheckCircle;
        } else if (lowerName.includes('cancel') || lowerName.includes('recus') || lowerName.includes('devolv')) {
            icon = faTimesCircle;
        } else if (lowerName.includes('envi') || lowerName.includes('trans') || lowerName.includes('caminho')) {
            icon = faTruck;
        } else if (lowerName.includes('andam') || lowerName.includes('prep') || lowerName.includes('separ')) {
            icon = faBoxOpen;
        }

        return (
            <span 
                style={{ 
                    backgroundColor: `${hex}15`,
                    color: hex,
                    border: `1px solid ${hex}35`
                }}
                className="px-3.5 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 w-fit shadow-xs"
            >
                <FontAwesomeIcon icon={icon} /> {name}
            </span>
        );
    };

    // Estatísticas calculadas
    const completedOrdersCount = orders.filter(o => {
        const s = (o.status?.name || '').toLowerCase();
        return s.includes('conclu') || s.includes('entreg') || s.includes('pago');
    }).length;

    const inProgressOrdersCount = orders.filter(o => {
        const s = (o.status?.name || '').toLowerCase();
        return !s.includes('conclu') && !s.includes('entreg') && !s.includes('cancel');
    }).length;

    if (isLoadingAuth) {
        return (
            <div className="min-h-screen bg-[var(--page-bg,#f8fafc)] flex flex-col items-center justify-center gap-3">
                <div className="animate-spin h-10 w-10 border-4 border-gray-200 border-t-[var(--primary)] rounded-full"></div>
                <p className="text-sm font-semibold text-gray-500">Acessando sua conta...</p>
            </div>
        );
    }

    return (
        <div className="bg-[var(--page-bg,#f8fafc)] min-h-screen flex flex-col font-sans">
            <Header />

            <main className="flex-grow container mx-auto px-4 py-8 md:py-12">
                
                {/* ======================================================== */}
                {/* --- ÁREA DO CLIENTE NÃO LOGADO (LOGIN / CADASTRO) --- */}
                {/* ======================================================== */}
                {!isAuthenticated && (
                    <div className="max-w-xl mx-auto">
                        
                        {/* Header de Boas-Vindas */}
                        <div className="text-center mb-8">
                            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[var(--primary)]/10 text-[var(--primary)] text-xs font-bold mb-3 border border-[var(--primary)]/20 shadow-xs">
                                <FontAwesomeIcon icon={faShieldAlt} /> Ambiente Seguro & Criptografado
                            </div>
                            <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight">
                                Minha Conta
                            </h1>
                            <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">
                                Acesse seus pedidos, acompanhe entregas e gerencie seus dados cadastrais.
                            </p>
                        </div>

                        {/* Card Principal */}
                        <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100/80 overflow-hidden backdrop-blur-sm">
                            
                            {/* Toggle Pílula Superior */}
                            <div className="p-2 bg-gray-50/80 border-b border-gray-100 flex gap-1">
                                <button 
                                    type="button"
                                    onClick={() => setIsLoginMode(true)} 
                                    className={`flex-1 py-3 px-4 text-sm font-bold rounded-2xl transition-all duration-200 flex items-center justify-center gap-2 ${
                                        isLoginMode 
                                            ? 'bg-white text-[var(--primary)] shadow-sm' 
                                            : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100/50'
                                    }`}
                                >
                                    <FontAwesomeIcon icon={faUser} /> Já tenho conta
                                </button>
                                <button 
                                    type="button"
                                    onClick={() => setIsLoginMode(false)} 
                                    className={`flex-1 py-3 px-4 text-sm font-bold rounded-2xl transition-all duration-200 flex items-center justify-center gap-2 ${
                                        !isLoginMode 
                                            ? 'bg-white text-[var(--primary)] shadow-sm' 
                                            : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100/50'
                                    }`}
                                >
                                    <FontAwesomeIcon icon={faShieldAlt} /> Criar nova conta
                                </button>
                            </div>

                            <div className="p-6 md:p-8">
                                
                                {/* FORMULÁRIO DE LOGIN */}
                                {isLoginMode ? (
                                    <form onSubmit={handleLogin} className="space-y-5">
                                        <div>
                                            <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                                                E-mail ou CPF/CNPJ
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                                                    <FontAwesomeIcon icon={faEnvelope} />
                                                </div>
                                                <input 
                                                    type="text" 
                                                    required
                                                    placeholder="seu@email.com ou 000.000.000-00"
                                                    className="w-full bg-gray-50/70 border border-gray-200 rounded-2xl pl-11 pr-4 py-3.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:bg-white transition-all"
                                                    value={loginData.login} 
                                                    onChange={e => setLoginData({...loginData, login: e.target.value})}
                                                />
                                            </div>
                                            <p className="text-[11px] text-gray-400 mt-1">
                                                Utilize o e-mail ou o documento cadastrado na sua compra.
                                            </p>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                                                Sua Senha
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                                                    <FontAwesomeIcon icon={faLock} />
                                                </div>
                                                <input 
                                                    type={showLoginPassword ? "text" : "password"} 
                                                    required
                                                    placeholder="Digite sua senha"
                                                    className="w-full bg-gray-50/70 border border-gray-200 rounded-2xl pl-11 pr-12 py-3.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:bg-white transition-all"
                                                    value={loginData.password} 
                                                    onChange={e => setLoginData({...loginData, password: e.target.value})}
                                                />
                                                <button 
                                                    type="button"
                                                    onClick={() => setShowLoginPassword(!showLoginPassword)}
                                                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                                                >
                                                    <FontAwesomeIcon icon={showLoginPassword ? faEyeSlash : faEye} />
                                                </button>
                                            </div>
                                        </div>

                                        <button 
                                            type="submit" 
                                            disabled={isSubmitting}
                                            className="w-full bg-[var(--primary)] text-white font-bold rounded-2xl py-4 mt-2 hover:brightness-110 active:scale-[0.99] transition-all shadow-lg shadow-[var(--primary)]/25 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-70"
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                    <span>Entrando...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <span>Entrar na Minha Conta</span>
                                                    <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                                                </>
                                            )}
                                        </button>
                                    </form>
                                ) : (
                                    /* FORMULÁRIO DE CADASTRO */
                                    <form onSubmit={handleRegister} className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                                                Nome Completo
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                                                    <FontAwesomeIcon icon={faUser} />
                                                </div>
                                                <input 
                                                    type="text" 
                                                    required 
                                                    placeholder="Ex: Maria Silva"
                                                    className="w-full bg-gray-50/70 border border-gray-200 rounded-2xl pl-11 pr-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:bg-white transition-all" 
                                                    value={registerData.name} 
                                                    onChange={e => setRegisterData({...registerData, name: e.target.value})} 
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                                                    E-mail
                                                </label>
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                                                        <FontAwesomeIcon icon={faEnvelope} />
                                                    </div>
                                                    <input 
                                                        type="email" 
                                                        required 
                                                        placeholder="seu@email.com"
                                                        className="w-full bg-gray-50/70 border border-gray-200 rounded-2xl pl-11 pr-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:bg-white transition-all" 
                                                        value={registerData.email} 
                                                        onChange={e => setRegisterData({...registerData, email: e.target.value})} 
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                                                    CPF ou CNPJ
                                                </label>
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                                                        <FontAwesomeIcon icon={faIdCard} />
                                                    </div>
                                                    <input 
                                                        type="text" 
                                                        required 
                                                        placeholder="Apenas números"
                                                        className="w-full bg-gray-50/70 border border-gray-200 rounded-2xl pl-11 pr-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:bg-white transition-all" 
                                                        value={registerData.document_number} 
                                                        onChange={e => setRegisterData({...registerData, document_number: e.target.value})} 
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                                                    WhatsApp / Celular
                                                </label>
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                                                        <FontAwesomeIcon icon={faPhone} />
                                                    </div>
                                                    <input 
                                                        type="text" 
                                                        required 
                                                        placeholder="(11) 90000-0000"
                                                        className="w-full bg-gray-50/70 border border-gray-200 rounded-2xl pl-11 pr-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:bg-white transition-all" 
                                                        value={registerData.phone} 
                                                        onChange={e => setRegisterData({...registerData, phone: e.target.value})} 
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                                                    Crie sua Senha
                                                </label>
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                                                        <FontAwesomeIcon icon={faLock} />
                                                    </div>
                                                    <input 
                                                        type={showRegisterPassword ? "text" : "password"} 
                                                        minLength={6} 
                                                        required 
                                                        placeholder="Mínimo 6 dígitos"
                                                        className="w-full bg-gray-50/70 border border-gray-200 rounded-2xl pl-11 pr-12 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:bg-white transition-all" 
                                                        value={registerData.password} 
                                                        onChange={e => setRegisterData({...registerData, password: e.target.value})} 
                                                    />
                                                    <button 
                                                        type="button"
                                                        onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                                                        className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                                                    >
                                                        <FontAwesomeIcon icon={showRegisterPassword ? faEyeSlash : faEye} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        <button 
                                            type="submit" 
                                            disabled={isSubmitting}
                                            className="w-full bg-[var(--primary)] text-white font-bold rounded-2xl py-4 mt-3 hover:brightness-110 active:scale-[0.99] transition-all shadow-lg shadow-[var(--primary)]/25 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-70"
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                    <span>Criando sua conta...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <span>Concluir Cadastro</span>
                                                    <FontAwesomeIcon icon={faCheckCircle} />
                                                </>
                                            )}
                                        </button>
                                    </form>
                                )}

                            </div>
                        </div>

                        {/* Benefícios no Rodapé do Form */}
                        <div className="grid grid-cols-3 gap-3 mt-8 text-center">
                            <div className="p-3 bg-white/60 rounded-2xl border border-gray-100 shadow-xs">
                                <FontAwesomeIcon icon={faTruck} className="text-[var(--primary)] text-lg mb-1 block mx-auto" />
                                <span className="text-[11px] font-bold text-gray-600 block">Rastreio Rápido</span>
                            </div>
                            <div className="p-3 bg-white/60 rounded-2xl border border-gray-100 shadow-xs">
                                <FontAwesomeIcon icon={faReceipt} className="text-[var(--primary)] text-lg mb-1 block mx-auto" />
                                <span className="text-[11px] font-bold text-gray-600 block">Segunda Via Pix</span>
                            </div>
                            <div className="p-3 bg-white/60 rounded-2xl border border-gray-100 shadow-xs">
                                <FontAwesomeIcon icon={faShieldAlt} className="text-[var(--primary)] text-lg mb-1 block mx-auto" />
                                <span className="text-[11px] font-bold text-gray-600 block">Compra Segura</span>
                            </div>
                        </div>

                    </div>
                )}

                {/* ======================================================== */}
                {/* --- ÁREA DO CLIENTE LOGADO (DASHBOARD) --- */}
                {/* ======================================================== */}
                {isAuthenticated && (
                    <div className="max-w-5xl mx-auto space-y-8 animate-fadeIn">
                        
                        {/* HERO CARD DO PERFIL */}
                        <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-gray-100/90 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-radial from-[var(--primary)]/10 to-transparent pointer-events-none -mr-16 -mt-16"></div>
                            
                            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative z-10">
                                <div className="flex items-center gap-4 md:gap-5">
                                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-gradient-to-br from-[var(--primary)]/20 to-[var(--primary)]/5 text-[var(--primary)] flex items-center justify-center text-2xl md:text-3xl font-black border-2 border-[var(--primary)]/30 overflow-hidden shadow-sm flex-shrink-0">
                                        {user?.avatar_url ? (
                                            <img src={user.avatar_url} alt={user.name} className="w-full h-full object-cover" />
                                        ) : (
                                            user?.name?.substring(0, 2).toUpperCase() || 'CL'
                                        )}
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h1 className="text-xl md:text-2xl font-black text-gray-900">
                                                {user?.name}
                                            </h1>
                                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-bold border border-emerald-200">
                                                <FontAwesomeIcon icon={faCheckCircle} className="text-[10px]" /> Cliente Ativo
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3 text-xs md:text-sm text-gray-500 mt-1 flex-wrap">
                                            <span className="flex items-center gap-1.5">
                                                <FontAwesomeIcon icon={faEnvelope} className="text-gray-400" />
                                                {user?.email || 'Sem e-mail'}
                                            </span>
                                            {user?.document_number && (
                                                <span className="flex items-center gap-1.5 font-mono">
                                                    <FontAwesomeIcon icon={faIdCard} className="text-gray-400" />
                                                    {user.document_number}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <button 
                                    onClick={logout} 
                                    className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold text-sm hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-all flex items-center gap-2 cursor-pointer self-stretch md:self-auto justify-center"
                                >
                                    <FontAwesomeIcon icon={faRightFromBracket} /> Sair da conta
                                </button>
                            </div>
                        </div>

                        {/* KPIS DE RESUMO RÁPIDO */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-xs flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-[var(--primary)]/10 text-[var(--primary)] flex items-center justify-center text-xl flex-shrink-0">
                                    <FontAwesomeIcon icon={faShoppingBag} />
                                </div>
                                <div>
                                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider block">Total de Compras</span>
                                    <span className="text-2xl font-black text-gray-900">{orders.length} pedidos</span>
                                </div>
                            </div>

                            <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-xs flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center text-xl flex-shrink-0">
                                    <FontAwesomeIcon icon={faClock} />
                                </div>
                                <div>
                                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider block">Em Andamento</span>
                                    <span className="text-2xl font-black text-gray-900">{inProgressOrdersCount} pedidos</span>
                                </div>
                            </div>

                            <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-xs flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center text-xl flex-shrink-0">
                                    <FontAwesomeIcon icon={faCheckCircle} />
                                </div>
                                <div>
                                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider block">Concluídos</span>
                                    <span className="text-2xl font-black text-gray-900">{completedOrdersCount} entregues</span>
                                </div>
                            </div>
                        </div>

                        {/* ABAS DO DASHBOARD */}
                        <div className="flex border-b border-gray-200 gap-6">
                            <button
                                type="button"
                                onClick={() => setActiveTab('pedidos')}
                                className={`pb-3 text-sm font-bold flex items-center gap-2 border-b-2 transition-all cursor-pointer ${
                                    activeTab === 'pedidos'
                                        ? 'border-[var(--primary)] text-[var(--primary)]'
                                        : 'border-transparent text-gray-500 hover:text-gray-800'
                                }`}
                            >
                                <FontAwesomeIcon icon={faBoxOpen} />
                                <span>Meus Pedidos</span>
                                <span className="ml-1 px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700">
                                    {orders.length}
                                </span>
                            </button>

                            <button
                                type="button"
                                onClick={() => setActiveTab('perfil')}
                                className={`pb-3 text-sm font-bold flex items-center gap-2 border-b-2 transition-all cursor-pointer ${
                                    activeTab === 'perfil'
                                        ? 'border-[var(--primary)] text-[var(--primary)]'
                                        : 'border-transparent text-gray-500 hover:text-gray-800'
                                }`}
                            >
                                <FontAwesomeIcon icon={faUser} />
                                <span>Meus Dados Cadastrais</span>
                            </button>
                        </div>

                        {/* ========================================= */}
                        {/* CONTEÚDO DA ABA PEDIDOS */}
                        {/* ========================================= */}
                        {activeTab === 'pedidos' && (
                            <div>
                                {isLoadingOrders ? (
                                    <div className="bg-white rounded-3xl p-12 flex flex-col items-center justify-center border border-gray-100 shadow-xs gap-3">
                                        <div className="animate-spin h-9 w-9 border-4 border-gray-200 border-t-[var(--primary)] rounded-full"></div>
                                        <p className="text-sm font-semibold text-gray-500">Buscando seus pedidos...</p>
                                    </div>
                                ) : orders.length > 0 ? (
                                    <div className="space-y-4">
                                        {orders.map((order) => {
                                            const firstItem = order.items?.[0]?.sellable;
                                            let imgUrl = 'https://placehold.co/400x400/f0f0f0/cccccc?text=Produto';
                                            
                                            if (firstItem) {
                                                imgUrl = firstItem.image_url || firstItem.product?.image_url || imgUrl;
                                            }

                                            return (
                                                <div 
                                                    key={order.id} 
                                                    className="bg-white rounded-3xl p-6 shadow-xs border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all duration-200"
                                                >
                                                    {/* Topo do Pedido */}
                                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-gray-100 pb-4 mb-4 gap-3">
                                                        <div>
                                                            <div className="flex items-center gap-2.5">
                                                                <span className="text-xs font-black uppercase tracking-wider text-gray-400">
                                                                    Pedido #{order.id}
                                                                </span>
                                                                <span className="text-gray-300">•</span>
                                                                <span className="text-xs font-semibold text-gray-500 flex items-center gap-1">
                                                                    <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-400" />
                                                                    {formatDate(order.created_at)}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            {getStatusBadge(order.status?.name, order.status?.color)}
                                                            <span className="text-xs font-bold text-gray-500 bg-gray-50 px-3 py-1.5 rounded-full border border-gray-100">
                                                                {order.payment_method?.name || 'Pagamento na Entrega/PDV'}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* Corpo do Pedido */}
                                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                                        <div className="flex items-center gap-4">
                                                            <div className="w-20 h-20 bg-gray-50/80 rounded-2xl border border-gray-100 p-2 flex-shrink-0 flex items-center justify-center">
                                                                <img src={imgUrl} className="w-full h-full object-contain mix-blend-multiply" alt="Produto" />
                                                            </div>
                                                            <div>
                                                                <h3 className="font-bold text-gray-900 text-sm md:text-base line-clamp-1">
                                                                    {firstItem?.product?.name || firstItem?.name || 'Item do Pedido'}
                                                                </h3>
                                                                <p className="text-xs text-gray-500 mt-0.5">
                                                                    {order.items?.[0]?.quantity || 1} unidade(s)
                                                                </p>
                                                                {order.items && order.items.length > 1 && (
                                                                    <p className="text-xs text-[var(--primary)] font-bold mt-1">
                                                                        + {order.items.length - 1} outro(s) item(ns) neste pedido
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-center border-t sm:border-t-0 pt-3 sm:pt-0 border-gray-50">
                                                            <div>
                                                                <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider block sm:text-right">Valor Total</span>
                                                                <span className="text-xl font-black text-gray-900 block sm:text-right">
                                                                    {formatPrice(Number(order.total_amount))}
                                                                </span>
                                                            </div>

                                                            <Link 
                                                                href={`/pedidos/${order.id}`}
                                                                className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gray-50 hover:bg-[var(--primary)] hover:text-white text-gray-700 text-xs font-bold transition-all border border-gray-200 hover:border-transparent cursor-pointer"
                                                            >
                                                                <span>Acompanhar Pedido</span>
                                                                <FontAwesomeIcon icon={faChevronRight} className="text-[10px]" />
                                                            </Link>
                                                        </div>
                                                    </div>

                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    /* Estado Vazio de Pedidos */
                                    <div className="bg-white rounded-3xl p-12 text-center shadow-xs border border-gray-100">
                                        <div className="bg-gray-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5 text-[var(--primary)]">
                                            <FontAwesomeIcon icon={faShoppingBag} className="text-3xl opacity-80" />
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-2">Nenhum pedido realizado ainda</h3>
                                        <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">
                                            Você ainda não realizou compras em nossa loja. Explore nosso catálogo e encontre os melhores produtos!
                                        </p>
                                        <Link 
                                            href="/buscar" 
                                            className="inline-flex items-center gap-2 px-8 py-3.5 bg-[var(--primary)] text-white font-bold text-sm rounded-xl hover:brightness-110 shadow-lg shadow-[var(--primary)]/20 active:scale-95 transition-all"
                                        >
                                            <span>Explorar Produtos</span>
                                            <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                                        </Link>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ========================================= */}
                        {/* CONTEÚDO DA ABA PERFIL / DADOS */}
                        {/* ========================================= */}
                        {activeTab === 'perfil' && (
                            <div className="bg-white rounded-3xl p-6 md:p-8 shadow-xs border border-gray-100 space-y-6">
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900 mb-1">Dados Cadastrais</h3>
                                    <p className="text-xs text-gray-500">Informações sincronizadas com o sistema da loja.</p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="p-4 rounded-2xl bg-gray-50/70 border border-gray-100">
                                        <span className="text-xs font-bold uppercase tracking-wider text-gray-400 block mb-1">Nome Completo</span>
                                        <span className="text-sm font-bold text-gray-800">{user?.name}</span>
                                    </div>

                                    <div className="p-4 rounded-2xl bg-gray-50/70 border border-gray-100">
                                        <span className="text-xs font-bold uppercase tracking-wider text-gray-400 block mb-1">E-mail Cadastrado</span>
                                        <span className="text-sm font-bold text-gray-800">{user?.email}</span>
                                    </div>

                                    <div className="p-4 rounded-2xl bg-gray-50/70 border border-gray-100">
                                        <span className="text-xs font-bold uppercase tracking-wider text-gray-400 block mb-1">CPF ou CNPJ</span>
                                        <span className="text-sm font-mono font-bold text-gray-800">{user?.document_number || 'Não informado'}</span>
                                    </div>

                                    <div className="p-4 rounded-2xl bg-gray-50/70 border border-gray-100">
                                        <span className="text-xs font-bold uppercase tracking-wider text-gray-400 block mb-1">Telefone / WhatsApp</span>
                                        <span className="text-sm font-bold text-gray-800">{user?.phone || 'Não informado'}</span>
                                    </div>
                                </div>

                                <div className="p-4 rounded-2xl bg-blue-50/60 border border-blue-100 flex items-start gap-3 text-xs text-blue-800">
                                    <FontAwesomeIcon icon={faShieldAlt} className="text-blue-500 mt-0.5 text-sm flex-shrink-0" />
                                    <p>
                                        Para alterar seus dados cadastrais, endereço de entrega ou redefinir sua senha, entre em contato com o suporte da loja. Seus dados estão seguros e protegidos.
                                    </p>
                                </div>
                            </div>
                        )}

                    </div>
                )}
            </main>

            <Footer />
        </div>
    );
}